import { Box, Divider, Tabs, Link as MUILink, Tab } from "@mui/material";
import * as React from "react";
import { useLanguage } from "./Language";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocationConfiguration } from "./LocationConfiguration";
import { useWindowVariables } from "./WindowVariables";
import { useLocation } from "react-router-dom";
import { MenuExternalLink, MenuLink } from "./AccountMenu";

export interface MainMenuProps {
  onClose: () => void;
}

export const MainMenu: React.FC<MainMenuProps> = ({ onClose }) => {
  const { translate } = useLanguage();
  const {
    currentVersion,
    tlcGoogleAnalyticsPrivacyURL,
    communityTrackingEnabled,
    customerTrackingEnabled,
  } = useWindowVariables();
  const {
    locationConfiguration: { pacSettings, helpSettings } = {
      pacSettings: null,
      helpSettings: null,
    },
  } = useLocationConfiguration();

  const location = useLocation();
  const currentPath = location.pathname;
  // important define these most specific to least specific
  const tabPaths = [
    "/search",
    "/advanced-search/boolean",
    "/advanced-search/reserves",
    "/advanced-search/reading-levels",
  ];

  const pathMatch = tabPaths.find((path) => currentPath.startsWith(path));
  const currentTab = pathMatch
    ? pathMatch
    : currentPath === "/"
    ? currentPath
    : false;

  const alternateTimeoutPageUrl =
    pacSettings?.useAlternateTimeoutPage &&
    pacSettings?.alternateTimeoutPageUrl;

  const helpLink = helpSettings?.localHelpURL
    ? helpSettings?.localHelpURL
    : `${helpSettings?.helpURLBase}${(currentVersion || "").replace(
        /\./g,
        "_"
      )}/UserApp/LS2PAC.htm`;

  const additionalLinks = [
    pacSettings?.additionalLink1,
    pacSettings?.additionalLink2,
    pacSettings?.additionalLink3,
    pacSettings?.additionalLink4,
    pacSettings?.additionalLink5,
  ].filter(
    ({ additionalLinkURL, additionalLinkText }) =>
      !!additionalLinkURL && !!additionalLinkText
  );

  return (
    <>
      <Tabs
        variant={"scrollable"}
        value={currentTab}
        orientation="vertical"
        textColor="secondary"
        indicatorColor="secondary"
        allowScrollButtonsMobile={true}
      >
        {alternateTimeoutPageUrl && (
          <MenuLink
            value={alternateTimeoutPageUrl}
            active={currentTab === alternateTimeoutPageUrl}
            label={translate("home")}
          />
        )}
        {!alternateTimeoutPageUrl && (
          <MenuLink
            value={"/"}
            active={currentTab === "/"}
            label={translate("home")}
          />
        )}
        <MenuLink
          value={"/search"}
          active={currentTab === "/search"}
          label={translate("search")}
        />
        <MenuLink
          value={"/advanced-search/boolean"}
          active={currentTab === "/advanced-search/boolean"}
          label={translate("booleanSearch")}
        />
        {(pacSettings?.acceleratedReaderEnabled ||
          pacSettings?.lexileEnabled) && (
          <MenuLink
            value={"/advanced-search/reading-levels"}
            active={currentTab === "/advanced-search/reading-levels"}
            label={translate("readingLevels")}
          />
        )}
        {pacSettings?.reservesEnabled && (
          <MenuLink
            value={"/reserves"}
            active={currentTab === "/reserves"}
            label={translate("academicReserves")}
          />
        )}
        {/* Only Tab components can be children of Tabs, we can't use Divider so we simulate with borderTop */}
        {/* {additionalLinks.length > 0 && <Divider />} */}
        {additionalLinks.map(
          ({ additionalLinkText, additionalLinkURL }, index) => (
            <MenuExternalLink
              divider={index === 0}
              key={additionalLinkURL}
              value={additionalLinkURL}
              onClick={(
                event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
              ) => {
                onClose();
              }}
              target={
                pacSettings?.displaySettings?.openLinksInNewTab
                  ? "_blank"
                  : "_self"
              }
              label={additionalLinkText}
              icon={
                pacSettings?.displaySettings?.openLinksInNewTab && (
                  <OpenInNewIcon sx={{ ml: 1 }} fontSize="small" />
                )
              }
            ></MenuExternalLink>
          )
        )}
        {/* <Divider /> */}
        <MenuExternalLink
          divider={true}
          active={currentTab === helpLink}
          value={helpLink}
          onClick={() => onClose()}
          label={translate("help")}
          target="_blank"
          icon={<HelpOutlineIcon fontSize="small" />}
        />
        {(communityTrackingEnabled || customerTrackingEnabled) && (
          <MenuExternalLink
            onClick={() => onClose()}
            active={currentTab === tlcGoogleAnalyticsPrivacyURL}
            value={tlcGoogleAnalyticsPrivacyURL}
            label={translate("privacy")}
            target="_blank"
            icon={<PrivacyTipIcon fontSize="small" />}
          />
        )}
      </Tabs>
      <Divider></Divider>
      <MUILink
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onClose()}
        href="https://tlcdelivers.com"
        sx={{
          height: "40px",
          width: "40px",
          margin: "8px 8px 8px 64px",
          padding: "12px 0",
          "&:hover .logo": {
            backgroundImage: `url('/images/TLC-logo.png')`,
          },
          "&:focus .logo": {
            backgroundImage: `url('/images/TLC-logo.png')`,
          },
        }}
      >
        <Box
          className="logo"
          sx={{
            width: "40px",
            height: "16px",
            textIndent: "-9999px",
            overflow: "hidden",
            backgroundSize: "40px 16px",
            backgroundImage: `url('/images/TLC-logo-grey.png')`,
            backgroundPosition: "0 0",
            backgroundRepeat: "no-repeat",
          }}
        >
          Visit TLC
        </Box>
      </MUILink>
    </>
  );
};
